import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, FormControl, InputLabel, Select, MenuItem, TextField, Button, CircularProgress, Paper, Grid, useTheme, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DatePicker from 'react-datepicker';
import { format, subDays, addMinutes } from 'date-fns';
import axios, { CancelTokenSource } from 'axios';
import { logdbInstance } from '../axiosInstance';
import Dygraph from 'dygraphs';
import 'react-datepicker/dist/react-datepicker.css';
import { SelectChangeEvent } from '@mui/material/Select';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SimCardIcon from '@mui/icons-material/SimCard';
import MemoryIcon from '@mui/icons-material/Memory';
import RouterIcon from '@mui/icons-material/Router';

interface ServiceChartsDialogProps {
    open: boolean;
    onClose: () => void;
    boxNumber: number;
}

const ServiceChartsDialog: React.FC<ServiceChartsDialogProps> = ({
    open,
    onClose,
    boxNumber
}) => {
    const theme = useTheme();
    const isDarkMode = theme.name === 'dark';
    const [serviceChartData, setServiceChartData] = useState<any[]>([]);
    const [loadingServiceData, setLoadingServiceData] = useState(false);
    const cancelTokenRef = useRef<CancelTokenSource | null>(null);
    const currentDeviceRef = useRef<number | null>(null);
    const [serviceTimeRange, setServiceTimeRange] = useState('2d');
    const [serviceStartDate, setServiceStartDate] = useState(subDays(new Date(), 2));
    const [serviceEndDate, setServiceEndDate] = useState(new Date());
    const [isCustomDateRange, setIsCustomDateRange] = useState(false);
    const [tempStartDate, setTempStartDate] = useState(subDays(new Date(), 2));
    const [tempEndDate, setTempEndDate] = useState(new Date());
    const loadUptimeChartRef = useRef<HTMLDivElement | null>(null);
    const loadUptimeInstance = useRef<Dygraph | null>(null);
    const metricsChartRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const dygraphInstances = useRef<{ [key: string]: Dygraph }>({});

    const metricColors = {
        PM10: '#8884d8',
        PM25: '#82ca9d',
        AMBIENT_TEMP: '#ff7300',
        AMBIENT_HUMID: '#0088FE',
        AMBIENT_PRESS: '#A52A2A',
        JAS: '#82ca9d',
        DUST_HEATER_NTC: '#FF6347',
        NH3_raw: '#9C27B0',
        H2S_raw: '#FF9800',
        ODO: '#00BCD4',
    };

    const uptimeToMinutes = (uptimeString: string): number => {
        if (uptimeString.includes(':')) {
            const [hours, minutes] = uptimeString.split(':').map(Number);
            return hours * 60 + minutes;
        }
        return Number(uptimeString);
    };

    const formatUptime = (uptimeString: string): string => {
        if (!uptimeString) return 'N/A';
        if (uptimeString.includes(':')) return uptimeString;
        return `0:${uptimeString.padStart(2, '0')}`;
    };

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return format(date, "yyyy-MM-dd HH:mm:ss");
        } catch (error) {
            return "Invalid Date";
        }
    };

    const formatLegendValue = (value: number | null): string => {
        if (value === null || value === undefined || isNaN(value)) return 'N/A';
        if (value > 1000) return value.toFixed(0);
        return value.toFixed(2);
    };

    const getBackgroundColor = (component: 'dialog' | 'chart' | 'paper' | 'card' = 'paper') => {
        if (isDarkMode) {
            switch (component) {
                case 'dialog':
                    return '#1a1a1a';
                case 'chart':
                    return '#2d2d2d';
                case 'paper':
                    return '#262626';
                case 'card':
                    return '#333333';
                default:
                    return '#262626';
            }
        }
        switch (component) {
            case 'dialog':
                return '#ffffff';
            case 'chart':
                return '#f8f8f8';
            case 'paper':
                return '#ffffff';
            case 'card':
                return '#f5f5f5';
            default:
                return '#ffffff';
        }
    };

    const barChartPlotter = (e: any) => {
        const ctx = e.drawingContext;
        const points = e.points;
        const y_bottom = e.dygraph.toDomYCoord(0);

        ctx.fillStyle = e.color;
        ctx.strokeStyle = e.color;

        let min_sep = Infinity;
        for (let i = 1; i < points.length; i++) {
            const sep = points[i].canvasx - points[i - 1].canvasx;
            if (sep < min_sep) min_sep = sep;
        }
        const bar_width = Math.floor(2.0 / 3 * min_sep);

        for (let i = 0; i < points.length; i++) {
            const p = points[i];
            const center_x = p.canvasx;

            ctx.fillRect(center_x - bar_width / 2,
                p.canvasy,
                bar_width,
                y_bottom - p.canvasy);

            ctx.strokeRect(center_x - bar_width / 2,
                p.canvasy,
                bar_width,
                y_bottom - p.canvasy);
        }
    };

    const fetchServiceData = useCallback(async (boxNumber: number) => {
        setLoadingServiceData(true);
        currentDeviceRef.current = boxNumber;

        if (cancelTokenRef.current) {
            cancelTokenRef.current.cancel('New request initiated');
        }

        cancelTokenRef.current = axios.CancelToken.source();

        try {
            const adjustedStartDate = new Date(serviceStartDate);
            adjustedStartDate.setHours(adjustedStartDate.getHours() - 1);

            const response = await logdbInstance.get(`/log`, {
                params: {
                    box: `eq.box${boxNumber}`,
                    time: [
                        `gte.${format(adjustedStartDate, "yyyy-MM-dd'T'HH:mm:ss")}`,
                        `lt.${format(serviceEndDate, "yyyy-MM-dd'T'HH:mm:ss")}`
                    ]
                },
                paramsSerializer: params => {
                    const parts = [];
                    for (const key in params) {
                        if (Array.isArray(params[key])) {
                            params[key].forEach((value: string) => {
                                parts.push(`${key}=${encodeURIComponent(value).replace(/%3A/g, ':')}`);
                            });
                        } else {
                            parts.push(`${key}=${encodeURIComponent(params[key])}`);
                        }
                    }
                    return parts.join('&');
                },
                cancelToken: cancelTokenRef.current.token
            });

            if (currentDeviceRef.current === boxNumber) {
                if (Array.isArray(response.data)) {
                    const formattedData = response.data.map((item: any) => {
                        const adjustedTime = new Date(item.time);
                        adjustedTime.setHours(adjustedTime.getHours() + 1);

                        return {
                            time: adjustedTime.toISOString(),
                            PM10: parseFloat(item.obj.PM10),
                            PM25: parseFloat(item.obj.PM25),
                            AMBIENT_TEMP: parseFloat(item.obj.AMBIENT_TEMP),
                            AMBIENT_HUMID: parseFloat(item.obj.AMBIENT_HUMID),
                            AMBIENT_PRESS: parseFloat(item.obj.AMBIENT_PRESS),
                            JAS_ABS: parseFloat(item.obj.JAS_ABS),
                            JAS_VALUE: parseFloat(item.obj.JAS_VALUE),
                            DUST_HEATER_NTC: parseFloat(item.obj.DUST_HEATER_NTC),
                            avg5mload: parseFloat(item.obj.avg5mload || '0'),
                            uptime: uptimeToMinutes(item.obj.uptime || '0'),
                            rawUptime: item.obj.uptime || '0',
                            imei: item.obj.imei || 'N/A',
                            imsi: item.obj.imsi || 'N/A',
                            iccid: item.obj.iccid || 'N/A',
                            mac: item.obj.mac || 'N/A',
                            NH3_raw: item.obj.NH3_raw ? parseFloat(item.obj.NH3_raw) : undefined,
                            H2S_raw: item.obj.H2S_raw ? parseFloat(item.obj.H2S_raw) : undefined,
                            ODO: item.obj.ODO ? parseFloat(item.obj.ODO) : undefined,
                        };
                    });

                    const fullRange = [];
                    let currentDate = serviceStartDate;
                    while (currentDate <= serviceEndDate) {
                        fullRange.push({
                            time: currentDate.toISOString(),
                            PM10: null,
                            PM25: null,
                            AMBIENT_TEMP: null,
                            AMBIENT_HUMID: null,
                            AMBIENT_PRESS: null,
                            JAS_ABS: null,
                            JAS_VALUE: null,
                            DUST_HEATER_NTC: null,
                            avg5mload: null,
                            uptime: null,
                            rawUptime: null,
                            imei: null,
                            imsi: null,
                            iccid: null,
                            mac: null,
                            NH3_raw: null,
                            H2S_raw: null,
                            ODO: null,
                        });
                        currentDate = addMinutes(currentDate, 5);
                    }

                    const mergedData = fullRange.map(rangeItem => {
                        const matchingItem = formattedData.find(dataItem =>
                            Math.abs(new Date(dataItem.time).getTime() - new Date(rangeItem.time).getTime()) < 150000
                        );
                        return matchingItem || rangeItem;
                    });

                    setServiceChartData(mergedData);
                } else {
                    setServiceChartData([]);
                }
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                setServiceChartData([]);
            }
        } finally {
            if (currentDeviceRef.current === boxNumber) {
                setLoadingServiceData(false);
            }
        }
    }, [serviceStartDate, serviceEndDate]);

    const handleServiceTimeRangeChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setServiceTimeRange(value);
        setIsCustomDateRange(value === 'custom');

        const endDate = new Date();
        let newStartDate;

        switch (value) {
            case '1d':
                newStartDate = subDays(endDate, 1);
                break;
            case '2d':
                newStartDate = subDays(endDate, 2);
                break;
            case '7d':
                newStartDate = subDays(endDate, 7);
                break;
            case '30d':
                newStartDate = subDays(endDate, 30);
                break;
            case 'custom':
                return;
            default:
                newStartDate = subDays(endDate, 2);
        }

        setServiceStartDate(newStartDate);
        setServiceEndDate(endDate);
        setTempStartDate(newStartDate);
        setTempEndDate(endDate);
    };

    const handleCustomDateChange = (date: Date | null, isStart: boolean) => {
        if (date) {
            if (isStart) {
                setTempStartDate(date);
            } else {
                setTempEndDate(date);
            }
        }
    };

    const applyCustomDateRange = () => {
        setServiceStartDate(tempStartDate);
        setServiceEndDate(tempEndDate);
    };

    const dygraphStyles = useMemo(() => `
    .dygraph-legend {
      position: absolute;
      font-size: 14px;
      z-index: 10;
      background: ${isDarkMode ? 'rgba(44, 44, 44, 0.95)' : 'rgba(255, 255, 255, 0.95)'};
      line-height: normal;
      text-align: left;
      overflow: visible;
      white-space: nowrap;
      padding: 8px 16px;
      border: ${isDarkMode ? '1px solid rgba(102, 102, 102, 0.5)' : '1px solid rgba(204, 204, 204, 0.5)'};
      border-radius: 4px;
      color: ${isDarkMode ? '#E0E0E0' : '#333'};
      box-shadow: ${isDarkMode ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.1)'};
      bottom: 40px !important;
      left: 50% !important;
      transform: translateX(-50%) !important;
      pointer-events: none;
      transition: border 0.1s ease-in-out;
      backdrop-filter: blur(2px);
      -webkit-backdrop-filter: blur(2px);
      will-change: transform;
      backface-visibility: hidden;
      height: fit-content !important;
      max-height: none !important;
    }
    
    .dygraph-legend > span {
      display: inline-block !important;
      margin-right: 15px !important;
      line-height: 24px !important;
    }
    
    .dygraph-legend > span:last-child {
      margin-right: 0 !important;
    }

    .dygraph-grid-line {
      stroke: ${isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)'} !important;
      stroke-width: 2px !important;
    }
  `, [isDarkMode]);

    useEffect(() => {
        const styleSheet = document.createElement('style');
        styleSheet.type = 'text/css';
        styleSheet.innerText = dygraphStyles;
        document.head.appendChild(styleSheet);

        return () => {
            document.head.removeChild(styleSheet);
        };
    }, [dygraphStyles]);

    useEffect(() => {
        if (open && boxNumber) {
            fetchServiceData(boxNumber);
        }

        return () => {
            if (cancelTokenRef.current) {
                cancelTokenRef.current.cancel('Component unmounted');
            }
        };
    }, [open, boxNumber, fetchServiceData]);

    useEffect(() => {
        if (!serviceChartData.length || !loadUptimeChartRef.current) return;

        const data = serviceChartData.map(item => [
            new Date(item.time),
            parseFloat(item.avg5mload) || null,
            parseFloat(item.uptime) || null
        ]);

        const options: any = {
            labels: ['Time', 'Load', 'Uptime'],
            width: loadUptimeChartRef.current.clientWidth,
            height: loadUptimeChartRef.current.clientHeight,
            gridLineColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
            gridLineWidth: 1,
            series: {
                'Load': {
                    axis: 'y1',
                    color: '#FFD700',
                    strokeWidth: 2,
                    plotter: barChartPlotter,
                    fillGraph: true,
                },
                'Uptime': {
                    axis: 'y2',
                    color: '#FF0000',
                    strokeWidth: 2,
                }
            },
            axes: {
                y: {
                    axisLabelWidth: 20,
                    axisLineColor: theme.palette.mode === 'dark' ? '#666' : '#ccc',
                    gridLineColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                    gridLineWidth: 1
                },
                y2: {
                    axisLabelWidth: 30,
                    axisLineColor: theme.palette.mode === 'dark' ? '#666' : '#ccc',
                    gridLineColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                    gridLineWidth: 1,
                    independentTicks: true
                },
                x: {
                    axisLineColor: theme.palette.mode === 'dark' ? '#666' : '#ccc',
                    gridLineColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                    gridLineWidth: 1
                }
            },
            animatedZooms: true,
            axisLabelColor: theme.palette.mode === 'dark' ? '#E0E0E0' : '#666',
            plotter: (e: any) => {
                if (e.seriesIndex === 0) {
                    barChartPlotter(e);
                } else {
                    Dygraph.Plotters.linePlotter(e);
                }
            },
            legendFormatter: (data: any) => {
                if (data.x == null) return '';
                const date = new Date(data.x);
                const formattedDateTime = format(date, "yyyy-MM-dd HH:mm:ss");
                let html = `<span style="font-weight: bold;">${formattedDateTime}</span>`;
                data.series.forEach((series: any) => {
                    if (series.isVisible) {
                        html += `<span style="margin-left: 15px; color: ${series.color}">
              ${series.labelHTML}: ${formatLegendValue(series.y)}
            </span>`;
                    }
                });
                return html;
            },
        };

        try {
            if (loadUptimeInstance.current) {
                loadUptimeInstance.current.updateOptions({
                    file: data,
                    ...options
                });
            } else {
                loadUptimeInstance.current = new Dygraph(
                    loadUptimeChartRef.current,
                    data,
                    options
                );
            }
        } catch (error) {
            console.error('Error initializing Load/Uptime chart:', error);
        }

        return () => {
            if (loadUptimeInstance.current) {
                loadUptimeInstance.current.destroy();
                loadUptimeInstance.current = null;
            }
        };
    }, [serviceChartData, theme.palette.mode, isDarkMode]);

    useEffect(() => {
        if (!serviceChartData.length) return;

        Object.entries(metricColors).forEach(([metric, color]) => {
            const element = metricsChartRefs.current[metric];
            if (!element) return;

            let data;
            if (metric === 'JAS') {
                data = serviceChartData.map(item => [
                    new Date(item.time),
                    item['JAS_ABS'] || null,
                    item['JAS_VALUE'] || null
                ]);
            } else if (metric !== 'JAS_ABS' && metric !== 'JAS_VALUE') {
                data = serviceChartData.map(item => [
                    new Date(item.time),
                    item[metric] || null
                ]);
            } else {
                return;
            }

            const options: any = {
                labels: metric === 'JAS' ? ['Time', 'JAS ABS', 'JAS VALUE'] : ['Time', metric],
                width: element.clientWidth,
                height: element.clientHeight,
                gridLineColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                gridLineWidth: 1,
                colors: metric === 'JAS' ? ['#82ca9d', '#FFFF00'] : [color],
                strokeWidth: 2,
                animatedZooms: true,
                axisLabelColor: theme.palette.mode === 'dark' ? '#E0E0E0' : '#666',
                series: metric === 'JAS' ? {
                    'JAS ABS': { axis: 'y1' },
                    'JAS VALUE': { axis: 'y2' }
                } : undefined,
                axes: {
                    y: {
                        axisLabelWidth: 35,
                        axisLineColor: theme.palette.mode === 'dark' ? '#666' : '#ccc',
                        gridLineColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                        gridLineWidth: 1
                    },
                    y2: metric === 'JAS' ? {
                        axisLabelWidth: 35,
                        axisLineColor: theme.palette.mode === 'dark' ? '#666' : '#ccc',
                        gridLineColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                        gridLineWidth: 1,
                        independentTicks: true
                    } : undefined,
                    x: {
                        axisLineColor: theme.palette.mode === 'dark' ? '#666' : '#ccc',
                        gridLineColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)',
                        gridLineWidth: 1
                    }
                },
                legendFormatter: (data: any) => {
                    if (data.x == null) return '';
                    const date = new Date(data.x);
                    const formattedDateTime = format(date, "yyyy-MM-dd HH:mm:ss");
                    let html = `<span style="font-weight: bold;">${formattedDateTime}</span>`;
                    data.series.forEach((series: any) => {
                        if (series.isVisible) {
                            html += `<span style="margin-left: 15px; color: ${series.color}">
                ${series.labelHTML}: ${formatLegendValue(series.y)}
              </span>`;
                        }
                    });
                    return html;
                },
                labelsSeparateLines: false,
                labelsKMB: true,
            };

            try {
                if (dygraphInstances.current[metric]) {
                    dygraphInstances.current[metric].updateOptions({
                        file: data,
                        ...options
                    });
                } else {
                    dygraphInstances.current[metric] = new Dygraph(
                        element,
                        data,
                        options
                    );
                }
            } catch (error) {
                console.error(`Error initializing ${metric} chart:`, error);
            }
        });

        return () => {
            Object.values(dygraphInstances.current).forEach(instance => {
                instance.destroy();
            });
            dygraphInstances.current = {};
        };
    }, [serviceChartData, theme.palette.mode, isDarkMode]);

    const renderMetricCharts = () => {
        const standardMetrics = ['PM10', 'PM25', 'AMBIENT_TEMP', 'AMBIENT_HUMID', 'AMBIENT_PRESS', 'DUST_HEATER_NTC', 'JAS'];
        const additionalMetrics = ['NH3_raw', 'H2S_raw', 'ODO'].filter(metric =>
            serviceChartData.some(item => item[metric] !== undefined && item[metric] !== null)
        );
        const allMetrics = [...standardMetrics, ...additionalMetrics];

        return (
            <Grid container spacing={3}>
                {allMetrics.map((metric) => {
                    if (metric === 'JAS_ABS' || metric === 'JAS_VALUE') return null;

                    return (
                        <Grid item xs={12} md={6} key={metric}>
                            <Paper sx={{
                                p: 2,
                                bgcolor: getBackgroundColor('paper'),
                                borderRadius: 2,
                            }}>
                                <Typography variant="h6" gutterBottom>
                                    {metric === 'NH3_raw' ? 'NH₃' :
                                        metric === 'H2S_raw' ? 'H₂S' :
                                            metric === 'ODO' ? 'ODO' :
                                                metric}
                                </Typography>
                                <Box sx={{ height: '25vh' }}>
                                    <div
                                        ref={el => metricsChartRefs.current[metric] = el}
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>
        );
    };

    const getLastNonNullValue = (data: any[], field: string): string => {
        for (let i = data.length - 1; i >= 0; i--) {
            if (data[i][field] !== null && data[i][field] !== undefined) {
                return data[i][field].toString();
            }
        }
        return 'N/A';
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: {
                    width: '95vw',
                    maxWidth: '95vw',
                    height: '90vh',
                    bgcolor: getBackgroundColor('dialog'),
                    border: '1px solid',
                    borderColor: 'borders.primary',
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle sx={{ bgcolor: getBackgroundColor('dialog'), pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" component="div">
                        Service Measurements for Box {boxNumber}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.mode === 'dark' ? '#fff' : 'inherit'
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{
                bgcolor: getBackgroundColor('dialog'),
                color: isDarkMode ? '#E0E0E0' : 'inherit',
                position: 'relative',
                p: 3,
            }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 3,
                        bgcolor: getBackgroundColor('paper'),
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'borders.primary',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="service-time-range-label">Time Range</InputLabel>
                            <Select
                                labelId="service-time-range-label"
                                value={serviceTimeRange}
                                onChange={handleServiceTimeRangeChange}
                                label="Time Range"
                            >
                                <MenuItem value="1d">1 Day</MenuItem>
                                <MenuItem value="2d">2 Days</MenuItem>
                                <MenuItem value="7d">7 Days</MenuItem>
                                <MenuItem value="30d">30 Days</MenuItem>
                                <MenuItem value="custom">Custom Range</MenuItem>
                            </Select>
                        </FormControl>

                        {isCustomDateRange && (
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                <DatePicker
                                    selected={tempStartDate}
                                    onChange={(date: Date) => handleCustomDateChange(date, true)}
                                    selectsStart
                                    startDate={tempStartDate}
                                    endDate={tempEndDate}
                                    customInput={
                                        <TextField
                                            size="small"
                                            label="Start Date"
                                            sx={{ minWidth: 200 }}
                                        />
                                    }
                                />
                                <DatePicker
                                    selected={tempEndDate}
                                    onChange={(date: Date) => handleCustomDateChange(date, false)}
                                    selectsEnd
                                    startDate={tempStartDate}
                                    endDate={tempEndDate}
                                    minDate={tempStartDate}
                                    customInput={
                                        <TextField
                                            size="small"
                                            label="End Date"
                                            sx={{ minWidth: 200 }}
                                        />
                                    }
                                />
                                <Button
                                    variant="contained"
                                    onClick={applyCustomDateRange}
                                    disabled={loadingServiceData}
                                    sx={{ height: 40 }}
                                >
                                    Apply
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{
                            p: 2,
                            height: '100%',
                            bgcolor: getBackgroundColor('paper'),
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'borders.primary',
                        }}>
                            <Typography variant="h6" gutterBottom>Device Details</Typography>
                            <Grid container spacing={2}>
                                {[
                                    { icon: <PhoneAndroidIcon />, label: 'IMEI', field: 'imei' },
                                    { icon: <SimCardIcon />, label: 'IMSI', field: 'imsi' },
                                    { icon: <MemoryIcon />, label: 'ICCID', field: 'iccid' },
                                    { icon: <RouterIcon />, label: 'MAC', field: 'mac' }
                                ].map((item) => (
                                    <Grid item xs={6} key={item.field}>
                                        <Box sx={{
                                            p: 2.5,
                                            bgcolor: getBackgroundColor('chart'),
                                            borderRadius: 1,
                                            height: '100%',
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                {React.cloneElement(item.icon, {
                                                    sx: { color: theme.palette.primary.main, mr: 1.5, fontSize: '1.5rem' }
                                                })}
                                                <Typography variant="h6">{item.label}</Typography>
                                            </Box>
                                            <Typography variant="body1" sx={{
                                                wordBreak: 'break-all',
                                                color: isDarkMode ? '#bdbdbd' : '#616161',
                                                fontSize: '1.1rem'
                                            }}>
                                                {getLastNonNullValue(serviceChartData, item.field)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Paper sx={{
                            p: 2,
                            height: '100%',
                            bgcolor: getBackgroundColor('paper'),
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'borders.primary',
                        }}>
                            <Typography variant="h6" gutterBottom>Load and Uptime</Typography>
                            <Box sx={{
                                height: '250px',
                                width: '100%',
                                position: 'relative',
                                '& > div': {
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    top: 0,
                                    bottom: 0,
                                }
                            }}>
                                <div
                                    ref={loadUptimeChartRef}
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {renderMetricCharts()}

                {loadingServiceData && (
                    <Box sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: isDarkMode 
                            ? 'rgba(0, 0, 0, 0.8)' 
                            : 'rgba(0, 0, 0, 0.6)',
                        zIndex: 9999,
                        overflow: 'hidden',
                    }}>
                        <CircularProgress 
                            size={60}
                            sx={{ 
                                color: isDarkMode ? '#fff' : '#1976d2'
                            }} 
                        />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ServiceChartsDialog;